import Lozenge from '@atlaskit/lozenge';
import { type ThemeColorModes, useThemeObserver } from '@atlaskit/tokens';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { JpdPrioritizationDarkIcon } from '../../assets/jpd-prioritization-dark';
import { JpdPrioritizationLightIcon } from '../../assets/jpd-prioritization-light';
import { JpdRoadmapDarkIcon } from '../../assets/jpd-roadmap-dark';
import { JpdRoadmapLightIcon } from '../../assets/jpd-roadmap-light';
import { JsmItsmDarkIcon } from '../../assets/jsm-itsm-dark';
import { JsmItsmLightIcon } from '../../assets/jsm-itsm-light';
import { JsmWorkRequestsDarkIcon } from '../../assets/jsm-work-requests-dark';
import { JsmWorkRequestsLightIcon } from '../../assets/jsm-work-requests-light';
import {
	COMPANY_MANAGED_GENERAL_SERVICE_PROJECT,
	JIRA_POLARIS_PRIORITIZATION,
	JIRA_POLARIS_ROADMAP,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC,
} from '../../implicit-message';
import { MenuItemBase } from '../menu-item/menu-item';
import { AdControls } from '@post-office/components--ad-controls';

type ProductKey = typeof Targets.JIRA_SERVICE_DESK | typeof Targets.JIRA_PRODUCT_DISCOVERY;

interface PlacementRenderProps {
	dismiss: () => void;
}
interface MessageProps {
	productKey: ProductKey;
	entityId: string;
	recommendationSessionId: string;
}

export type Nav4RecommendationProps = MessageProps & PlacementRenderProps;

const itsmLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmItsmLightIcon /> : <JsmItsmDarkIcon />;
};

const workRequestLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmWorkRequestsLightIcon /> : <JsmWorkRequestsDarkIcon />;
};

const jpdRoadmapLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JpdRoadmapLightIcon /> : <JpdRoadmapDarkIcon />;
};

const jpdPrioritizationLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JpdPrioritizationLightIcon /> : <JpdPrioritizationDarkIcon />;
};

const iconMap: Record<string, (colorMode?: ThemeColorModes) => JSX.Element> = {
	[COMPANY_MANAGED_GENERAL_SERVICE_PROJECT]: workRequestLogoByTheme,
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC]: itsmLogoByTheme,
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED]: itsmLogoByTheme,
	[JIRA_POLARIS_ROADMAP]: jpdRoadmapLogoByTheme,
	[JIRA_POLARIS_PRIORITIZATION]: jpdPrioritizationLogoByTheme,
};

const templateNameMap: Record<string, JSX.Element> = {
	[COMPANY_MANAGED_GENERAL_SERVICE_PROJECT]: (
		<FormattedMessage
			id="cross-flow-work-requests-template.jsm.title"
			defaultMessage="Collect requests"
			description="Jira Service Management Work Requests template display name"
		/>
	),
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC]: (
		<FormattedMessage
			id="cross-flow-itsm-template.jsm.title"
			defaultMessage="Collect IT requests"
			description="Jira Service Management ITSM template display name"
		/>
	),
	[SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED]: (
		<FormattedMessage
			id="cross-flow-itsm-template.jsm.title"
			defaultMessage="Collect IT requests"
			description="Jira Service Management ITSM template display name"
		/>
	),
	[JIRA_POLARIS_ROADMAP]: (
		<FormattedMessage
			id="cross-flow-product-roadmap-template.jpd.title"
			defaultMessage="Create a roadmap"
			description="Jira Product Discovery product roadmap template display name"
		/>
	),
	[JIRA_POLARIS_PRIORITIZATION]: (
		<FormattedMessage
			id="cross-flow-product-prioritization-template.jpd.title"
			defaultMessage="Prioritize ideas"
			description="Jira Product Discovery product prioritization template display name"
		/>
	),
};

const productNameMap: Record<ProductKey, string> = {
	[Targets.JIRA_SERVICE_DESK]: 'Jira Service Management',
	[Targets.JIRA_PRODUCT_DISCOVERY]: 'Jira Product Discovery',
};

export const Nav4Recommendation = ({
	productKey,
	entityId,
	recommendationSessionId,
	dismiss,
}: Nav4RecommendationProps) => {
	const { colorMode } = useThemeObserver();
	const icon = iconMap[entityId]?.(colorMode);
	const templateName = templateNameMap[entityId];
	const crossFlow = useCrossFlow();

	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const { messageInstanceClicked } = useMessageInstanceEvent();

	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'projectsMenu',
			},
			{
				source: 'projectsMenuSection',
				recommendedProductIds: [productKey],
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
			},
			{
				sqa1052Enabled: true,
			},
		);
	}, [fireViewedUIEvent, productKey, recommendationSessionId]);

	const handleClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'recommendation',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'react',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
			},
		});

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryThisRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: productKey,
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
				recommendedProductIds: [productKey],
			},
			{
				sqa1052Enabled: true,
			},
		);

		if (crossFlow.isEnabled) {
			void crossFlow.apiV1.open({
				v1: true,
				sourceComponent: 'projectsMenu',
				sourceContext: 'tryThisRecommendation',
				targetProduct: productKey,
				journey: Journeys.GET_STARTED,
				extensions: {
					provisioningPageOptions: {
						actions: {
							primary: {
								behaviour: 'land',
								prompt: 'next',
								options: {
									targetTab: 'same',
									urlParams: { templateId: entityId },
								},
							},
						},
					},
					recommendationContext: {
						recommendationSessionId,
						entityId,
					},
				},
			});
		}
	}, [
		crossFlow,
		entityId,
		fireInteractedUIEvent,
		messageInstanceClicked,
		productKey,
		recommendationSessionId,
	]);

	const handleDismiss = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
			},
		});

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: productKey,
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
				recommendedProductIds: [productKey],
			},
			{
				sqa1052Enabled: true,
			},
		);
		dismiss();
	}, [dismiss, messageInstanceClicked, recommendationSessionId, productKey, fireInteractedUIEvent]);

	return (
		<div role="listitem">
			<MenuItemBase
				elemBefore={icon}
				elemAfter={
					<Lozenge appearance="new" isBold={false}>
						<FormattedMessage
							id="post-office.nav-4-projects-recommendations.cta-button-lozenge-try"
							defaultMessage="Try"
							description="A lozenge to display the try button"
						/>
					</Lozenge>
				}
				href=""
				onClick={handleClick}
				actionsOnHover={
					<AdControls
						iconSize="small"
						onDismissed={handleDismiss}
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							message: (
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-explanation"
									defaultMessage="We think {productName} will improve your team's productivity. The recommendation is based on:"
									description="Explanation as to why the placeholder template is being shown"
									values={{ productName: <strong>{productNameMap[productKey]}</strong> }}
								/>
							),
							reasons: [
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-bullet-one-spork"
									defaultMessage="the tools you use (Jira)"
									description="First bullet point of the reasons modal"
								/>,
								<FormattedMessage
									id="post-office.nav-4-projects-recommendations.reasons-modal-bullet-two-appify"
									defaultMessage="the way you use your existing tools and apps"
									description="Second bullet point of the reasons modal"
								/>,
							],
						}}
						additionalAnalyticsAttributes={{
							source: 'projectsMenuSection',
							targetProductId: productKey,
							sqa1052Enabled: 'true',
						}}
					/>
				}
			>
				{templateName}
			</MenuItemBase>
		</div>
	);
};
